import React, {memo} from 'react';
import {number, object} from 'prop-types';

import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import DataUsageIcon from '@mui/icons-material/DataUsage';
import Grid from '@mui/material/Unstable_Grid2';
import GroupIcon from '@mui/icons-material/Group';
import {Button, Chip, Paper, Stack, Tooltip, Typography} from '@mui/material';

import {trackEvent} from '@app/analytics';
import {useTranslation} from 'react-i18next';

import AreaIndicator from '@app/components/common/AreaIndicator';
import CollectionToolbar from './CollectionToolbar.jsx';
import WarningIcon from '@mui/icons-material/Warning';
import useCollectionParams from '@app/hooks/useCollectionParams';
import {TOURS, TOUR_STAGES} from '@app/constants';
import {fitExtent} from '@app/actions/map';
import {setTourIndex} from '@app/actions/tour';
import {useDispatch, useSelector} from 'react-redux';
import {useGetReservedProductsForCollection} from '@api/quotaReservations.js';

const CollectionItem = ({collection, productCount}) => {
  const {t} = useTranslation();
  const dispatch = useDispatch();
  const {setCollection} = useCollectionParams();
  const isTourRunning = useSelector(
    state => state.tour.run && state.tour.tour === TOURS.COLLECTION_TOUR_PT1
  );

  const {data: reservedProducts, isSuccess} =
    useGetReservedProductsForCollection(collection.id);

  const handleOpenClick = () => {
    trackEvent('Collection Item', {
      action: 'Opened',
      collectionId: collection.id,
    });
    setCollection(collection.id);

    if (collection.feature_count > 0 && collection.extent) {
      dispatch(fitExtent(collection.extent?.spatial?.bbox[0]));
    }

    if (isTourRunning) {
      dispatch(setTourIndex(1));
    }
  };

  return (
    <Paper
      variant="outlined"
      elevation={0}
      sx={{
        paddingBottom: 1,
        paddingLeft: 1,
      }}
    >
      <Grid container>
        <Grid sm={9} md={9} lg={9} display="flex" alignItems="center">
          <Typography
            fontWeight={500}
            textOverflow="ellipsis"
            overflow="hidden"
            noWrap
          >
            {`${collection.title}`}
          </Typography>
        </Grid>
        <Grid sm={3} md={3} lg={3}>
          <CollectionToolbar
            collection={collection}
            hasProducts={productCount > 0}
          />
        </Grid>
        <Grid sm={12} md={12} lg={12} display="flex" alignItems="center">
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2" color="textSecondary">
              {t('collectionToolbar.featureCount', {
                count: collection.feature_count,
              })}
            </Typography>
            <Typography color="textSecondary">•</Typography>
            <Typography variant="body2" color="textSecondary">
              {t('collectionToolbar.totalArea')}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              <AreaIndicator squareMeters={collection.area} />
            </Typography>
          </Stack>
        </Grid>
        <Grid
          sm={12}
          md={12}
          lg={12}
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          sx={{
            paddingRight: 1,
          }}
        >
          <Stack direction="row" spacing={1}>
            {isSuccess && reservedProducts?.length > 0 && (
              <Tooltip
                title={reservedProducts.map(product => product.title).join(' ')}
              >
                <Chip
                  color="primary"
                  icon={<DataUsageIcon />}
                  label={`${reservedProducts.length}/${productCount} products`}
                  sx={{
                    backgroundColor: 'primary.lighter',
                    color: 'primary.main',
                  }}
                />
              </Tooltip>
            )}
            {collection.permissions?.is_owner && <Chip label="Owner" />}
            {collection.permissions?.shared && (
              <Tooltip
                title={t('collectionToolbar.shareChipTitle')}
                placement="top"
              >
                <Chip icon={<GroupIcon fontSize="small" color="action" />} />
              </Tooltip>
            )}

            {collection.feature_count === 0 && (
              <Tooltip
                title={t('collectionToolbar.emptyCollectionChipTitle')}
                placement="top-end"
              >
                <Chip
                  icon={<WarningIcon fontSize="small" color="warning" />}
                  sx={{backgroundColor: theme => theme.palette.warning.light}}
                />
              </Tooltip>
            )}
          </Stack>
          <Button
            onClick={() => handleOpenClick()}
            color="primary"
            variant="outlined"
            endIcon={<ArrowForwardIcon />}
            sx={{
              height: theme => theme.spacing(3.75),
              minWidth: theme => theme.spacing(10),
            }}
            data-tour={TOUR_STAGES.OPEN_COLLECTION}
          >
            {t('collectionToolbar.open')}
          </Button>
        </Grid>
      </Grid>
    </Paper>
  );
};

CollectionItem.propTypes = {
  collection: object.isRequired,
  productCount: number,
};

export default memo(CollectionItem);
